/* eslint-disable @typescript-eslint/ban-types */
/// <reference types="webpack-env" />
import { set } from 'lodash';

/**
 * Utility function that mimics require-all's result with an object where the keys are the imported files
 * require.context cannot be added to this because it relies on static analysis to resolve dependencies
 * which breaks if a variable is used for its path
 * https://webpack.js.org/guides/dependency-management/#context-module-api
 *
 * @param context result from require.context
 * @returns an object with the imports following the same hierarchy as their file system layout
 */
export const importAll = (context: ReturnType<typeof require.context>) => {
  const cache: { [index: string]: object } = {};
  context.keys().forEach((key: string) => {
    // remove the initial ./ and extension in the file path
    // then replace all / with . so that lodash's set can deeply set it
    const filepath = key.substr(2, key.lastIndexOf('.') - 2).replace('/', '.');
    const module = context(key);
    set(cache, filepath, module);
  });
  return cache;
};

/**
 * Same as importAll, except returns a flat map.
 *
 * @param keyTransform a function that transforms the import's file path to the key in the returned object
 * @returns an object with the imports flatly listed
 * */
export const importAllFlat = (
  context: ReturnType<typeof require.context>,
  keyTransform = (s: string) => s,
) => {
  const cache: { [index: string]: object } = {};
  context.keys().forEach((key: string) => {
    cache[keyTransform(key)] = context(key);
  });
  return cache;
};

// This implementation is needed instead of lodash's isEmpty because it
// allows TypeScript to assert that a variable is not null or undefined
// https://www.typescriptlang.org/docs/handbook/advanced-types.html#type-guards-and-differentiating-types
// https://stackoverflow.com/a/52097700
export function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

// If the given value is null, this converts it to undefined
// Otherwise the value is returned
export function removeNull<T>(value: T | undefined | null): T | undefined {
  return value !== null ? value : undefined;
}

// Usage: parseEnumValue(EnumType, PotentialEnumValue)
// Returns PotentialEnumValue if it's a value in EnumType, otherwise undefined is returned
export function parseEnumValue<T>(
  myEnum: { [k: string]: T },
  value: T | undefined,
): T | undefined {
  return Object.values(myEnum).find(x => x === value);
}

// overload isFinite to add a type guard
declare module 'lodash' {
  interface LoDashStatic {
    isFinite(value: unknown): value is number;
  }
}

/**
 * Create a promise to simulate sleep delay.
 *
 * @param ms number of milliseconds to wait before resolving
 */
export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Type for unwrapping a Promise's type
 * https://stackoverflow.com/questions/48011353/how-to-unwrap-type-of-a-promise
 */
export type ThenArg<T> = T extends Promise<infer U>
  ? U
  : T extends (...args: any[]) => Promise<infer U>
  ? U
  : T;

/**
 * Type guard to check if a value is a key of an enum.
 */
export function isKeyOfEnum<T extends Record<string, unknown>>(
  enumeration: T,
  key: unknown,
): key is keyof T {
  return typeof key === 'string' && key in enumeration;
}
